// Runtime-owned UI text (7.6I): the accessible names and the few visible words
// that belong to the runtime's own controls (media controls, carousel arrows
// and dots, survey "Next", field errors, game announcements) — never document
// content. Shared by the Studio preview (Spanish, the product's language) and
// the served ad, which picks the strings from the document's `meta.locale`
// (Spanish by default; English for `en*`). Plain strings only: they are always
// set as text / attribute values, never as markup.

import type { CreativeDocument, Element, TextContent } from "@interactads/creative-model";
import type { GameElementView, GameNotice, InteractionEngine } from "./engine.js";

export interface RuntimeStrings {
  readonly video: string;
  readonly audio: string;
  readonly play: (name: string) => string;
  readonly pause: (name: string) => string;
  readonly unmute: string;
  readonly mute: string;
  readonly unmuteOf: (name: string) => string;
  readonly carousel: string;
  readonly carouselRole: string;
  readonly slideRole: string;
  readonly slideOf: (i: number, n: number) => string;
  readonly prevSlide: string;
  readonly nextSlide: string;
  readonly goToSlide: (i: number) => string;
  readonly pauseCarousel: string;
  readonly resumeCarousel: string;
  readonly answerRequired: string;
  readonly chooseOption: string;
  readonly ofScale: (v: number, max: number) => string;
  readonly next: string;
  readonly skip: string;
  readonly chooseUpTo: (n: number) => string;
  readonly progress: string;
  readonly progressPct: (pct: number) => string;
  readonly timeLeft: string;
  readonly hotspot: string;
  readonly openDestination: string;
  readonly element: string;
  readonly faceDown: string;
  readonly matchedSuffix: string;
  readonly foundSuffix: string;
  readonly placedSuffix: string;
  readonly pickedSuffix: string;
  readonly dropOn: (name: string) => string;
  readonly runnerKeys: string;
  readonly notice: {
    readonly start: string;
    readonly hit: string;
    readonly miss: string;
    readonly match: string;
    readonly mismatch: string;
    readonly drop: string;
    readonly reject: string;
    readonly win: string;
    readonly lose: string;
    readonly timeout: string;
    readonly pickup: string;
    readonly hazard: string;
    readonly segment: string;
    readonly points: (n: number) => string;
    readonly lives: (n: number) => string;
    readonly energy: (n: number) => string;
    readonly pairsOf: (a: number, b: number) => string;
  };
}

const n = String;

export const RUNTIME_STRINGS_ES: RuntimeStrings = {
  video: "Video",
  audio: "Audio",
  play: (x) => `Reproducir ${x}`,
  pause: (x) => `Pausar ${x}`,
  unmute: "Activar sonido",
  mute: "Silenciar",
  unmuteOf: (x) => `Activar el sonido de ${x}`,
  carousel: "Carrusel",
  carouselRole: "carrusel",
  slideRole: "diapositiva",
  slideOf: (i, t) => `${n(i)} de ${n(t)}`,
  prevSlide: "Diapositiva anterior",
  nextSlide: "Diapositiva siguiente",
  goToSlide: (i) => `Ir a la diapositiva ${n(i)}`,
  pauseCarousel: "Pausar el carrusel",
  resumeCarousel: "Reanudar el carrusel",
  answerRequired: "Responde esta pregunta.",
  chooseOption: "Elige una opción…",
  ofScale: (v, max) => `${n(v)} de ${n(max)}`,
  next: "Siguiente",
  skip: "Omitir",
  chooseUpTo: (x) => `Elige hasta ${n(x)}.`,
  progress: "Progreso",
  progressPct: (p) => `Progreso: ${n(p)} %`,
  timeLeft: "Tiempo restante",
  hotspot: "Zona clicable",
  openDestination: "Abrir destino",
  element: "Elemento",
  faceDown: "boca abajo",
  matchedSuffix: " · pareja encontrada",
  foundSuffix: " · encontrada",
  placedSuffix: " · colocada",
  pickedSuffix: " · tomada (elige su lugar)",
  dropOn: (x) => `Soltar en ${x}`,
  runnerKeys: "Juego: Espacio o flecha arriba para saltar, flecha abajo para agacharse",
  notice: {
    start: "Empieza el juego.",
    hit: "¡Acierto!",
    miss: "Fallo.",
    match: "¡Pareja encontrada!",
    mismatch: "No son pareja.",
    drop: "Pieza en su lugar.",
    reject: "Ese no es su lugar.",
    win: "¡Ganaste!",
    lose: "Fin del juego: perdiste.",
    timeout: "Se acabó el tiempo.",
    pickup: "¡Recogido!",
    hazard: "¡Golpe!",
    segment: "Nuevo escenario",
    points: (x) => ` Puntos: ${n(x)}.`,
    lives: (x) => ` Vidas: ${n(x)}.`,
    energy: (x) => ` Energía: ${n(x)}.`,
    pairsOf: (a, b) => ` ${n(a)} de ${n(b)}.`,
  },
};

export const RUNTIME_STRINGS_EN: RuntimeStrings = {
  video: "Video",
  audio: "Audio",
  play: (x) => `Play ${x}`,
  pause: (x) => `Pause ${x}`,
  unmute: "Unmute",
  mute: "Mute",
  unmuteOf: (x) => `Unmute ${x}`,
  carousel: "Carousel",
  carouselRole: "carousel",
  slideRole: "slide",
  slideOf: (i, t) => `${n(i)} of ${n(t)}`,
  prevSlide: "Previous slide",
  nextSlide: "Next slide",
  goToSlide: (i) => `Go to slide ${n(i)}`,
  pauseCarousel: "Pause the carousel",
  resumeCarousel: "Resume the carousel",
  answerRequired: "Please answer this question.",
  chooseOption: "Choose an option…",
  ofScale: (v, max) => `${n(v)} of ${n(max)}`,
  next: "Next",
  skip: "Skip",
  chooseUpTo: (x) => `Choose up to ${n(x)}.`,
  progress: "Progress",
  progressPct: (p) => `Progress: ${n(p)} %`,
  timeLeft: "Time left",
  hotspot: "Clickable area",
  openDestination: "Open destination",
  element: "Element",
  faceDown: "face down",
  matchedSuffix: " · pair found",
  foundSuffix: " · found",
  placedSuffix: " · placed",
  pickedSuffix: " · picked (choose its place)",
  dropOn: (x) => `Drop on ${x}`,
  runnerKeys: "Game: Space or Up arrow to jump, Down arrow to duck",
  notice: {
    start: "The game starts.",
    hit: "Hit!",
    miss: "Miss.",
    match: "Pair found!",
    mismatch: "Not a pair.",
    drop: "Piece in place.",
    reject: "That is not its place.",
    win: "You won!",
    lose: "Game over: you lost.",
    timeout: "Time is up.",
    pickup: "Picked up!",
    hazard: "Hit!",
    segment: "New stage",
    points: (x) => ` Points: ${n(x)}.`,
    lives: (x) => ` Lives: ${n(x)}.`,
    energy: (x) => ` Energy: ${n(x)}.`,
    pairsOf: (a, b) => ` ${n(a)} of ${n(b)}.`,
  },
};

/** Strings of the runtime UI for a document locale (BCP 47): English for `en*`, Spanish otherwise. */
export function runtimeStrings(locale: string | undefined): RuntimeStrings {
  return /^en\b/i.test(locale ?? "") ? RUNTIME_STRINGS_EN : RUNTIME_STRINGS_ES;
}

/** Plain text of a TextContent (bound runs excluded). */
export const plain = (t: TextContent | undefined) => (t?.runs ?? []).map((r) => ("text" in r ? r.text : "")).join("");

/** Value of a `bind` text run (same formats in the Studio and the served ad). */
export function formatBoundValue(value: unknown, format: "integer" | "decimal1" | "mmss"): string {
  const v = typeof value === "number" ? value : 0;
  if (format === "decimal1") return v.toFixed(1);
  if (format === "mmss") {
    const total = Math.max(0, Math.round(v / 1_000));
    return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, "0")}`;
  }
  return String(Math.round(v));
}

/** Digits of a remaining time: "ss" = whole seconds, "mm:ss"; rounded UP so 0 shows only at the end. */
export function countdownText(ms: number, format: "ss" | "mm:ss"): string {
  const total = Math.max(0, Math.ceil(ms / 1_000));
  return format === "ss" ? String(total) : `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, "0")}`;
}

/** What a card / target / piece says to assistive technology (its state, never a hidden face). */
export function gameLabel(
  el: Element,
  base: string,
  game: GameElementView | null,
  picked: string | null,
  t: RuntimeStrings = RUNTIME_STRINGS_ES
): string {
  if (!game) return base;
  const face =
    el.type === "text" || el.type === "button"
      ? plain(el.type === "text" ? el.content : el.label)
      : el.type === "image"
        ? el.alt
        : "";
  switch (game.role) {
    case "card":
      return game.face === "down"
        ? `${base}, ${t.faceDown}`
        : `${base}: ${face}${game.face === "matched" ? t.matchedSuffix : ""}`;
    case "target":
      return game.found ? `${base}${t.foundSuffix}` : base;
    case "item":
      return game.placedIn ? `${base}${t.placedSuffix}` : game.picked ? `${base}${t.pickedSuffix}` : base;
    case "zone":
      return picked !== null ? t.dropOn(base) : base;
  }
}

/** Plain-language announcement of a game notice (polite live region; never one per second or per frame). */
export function gameNoticeText(
  e: InteractionEngine,
  notice: GameNotice,
  t: RuntimeStrings = RUNTIME_STRINGS_ES
): string {
  const g: CreativeDocument["game"] = e.game?.decl;
  const num = (id: string | undefined) => {
    const v = id === undefined ? undefined : e.variable(id);
    return typeof v === "number" ? v : undefined;
  };
  const score = num(g?.score);
  const lives = num(g?.lives);
  const energy = g?.runner?.energy ? num(g.runner.energy.var) : undefined;
  const x = t.notice;
  switch (notice.event) {
    case "start":
      return x.start;
    case "hit":
      return `${x.hit}${score !== undefined ? x.points(score) : ""}`;
    case "miss":
      return `${x.miss}${lives !== undefined ? x.lives(lives) : ""}`;
    case "match": {
      const p = e.game?.pairsProgress();
      return `${x.match}${p ? x.pairsOf(p.matched, p.total) : ""}`;
    }
    case "mismatch":
      return x.mismatch;
    case "drop":
      return x.drop;
    case "reject":
      return `${x.reject}${lives !== undefined ? x.lives(lives) : ""}`;
    case "win":
      return x.win;
    case "lose":
      return x.lose;
    case "timeout":
      return x.timeout;
    // Runner (7.6F.1): jumps and ducks are the player's own gestures (never announced); contacts
    // and environments are, at most a few per run segment (bounded by the spawn intervals).
    case "jump":
    case "duck":
      return "";
    case "pickup":
      return `${x.pickup}${energy !== undefined ? x.energy(energy) : score !== undefined ? x.points(score) : ""}`;
    case "hazard":
      return `${x.hazard}${energy !== undefined ? x.energy(energy) : lives !== undefined ? x.lives(lives) : ""}`;
    case "segment": {
      const seg = g?.runner?.segments.find((s) => s.id === notice.target);
      return `${x.segment}${seg?.name ? `: ${seg.name}` : ""}.`;
    }
  }
}
